"use client";

import { menu } from "@/data";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { useAbout } from "@/context/about";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";

type MenuProps = {
  orientation?: "horizontal" | "vertical";
  size?: "normal" | "large";
  align?: "left" | "center" | "right";
  className?: string;
  onClick?: () => void;
}

function Menu({ orientation = "horizontal", size = "normal", align = "left", className, onClick }: MenuProps) {
  const { isOpen, toggle } = useAbout();
  const pathname = usePathname();
  const locale = useLocale();
  
  // Check if we're not on the homepage
  const isNotHomePage = pathname && pathname !== '/' && !pathname.match(/^\/[a-z]{2}$/);
  
  const handleItemClick = (href: string) => {
    // Handle the special case for About section
    if (href === "#about" && !isOpen) {
      toggle(); // Open the About section if it's closed
      
      // Small delay to ensure DOM is updated before scrolling
      setTimeout(() => {
        const aboutSection = document.getElementById('about');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    
    // Call the parent onClick handler if provided
    if (onClick) {
      onClick();
    }
  };
  
  return (
    <nav className={cn("flex gap-5 text-sm", orientation === "vertical" && "flex-col", size === "large" && "text-lg", align === "center" && "justify-center items-center", align === "right" && "justify-end", className)}>
      {menu.map((item) => {
        // For internal links (sections), make them absolute when not on homepage
        let href = item.href;
        if (!item.external && item.href.startsWith('#') && isNotHomePage) {
          // Convert to absolute URL to go back to homepage with correct section
          href = (locale === 'en' ? '/' : `/${locale}`) + item.href;
        }
        
        return (
          <Link
            href={href}
            key={item.name}
            target={item.external ? "_blank" : ""}
            scroll={!item.external}
            onClick={() => handleItemClick(item.href)}
            className="text-foreground/80 hover:text-foreground flex items-center"
          >
            {item.name}
            {item.external && <ArrowUpRight className="inline-block ml-[2px] w-4 h-4" />}
          </Link>
        );
      })}
    </nav>
  );
}

export default Menu;